/**
 * ASKING THE SERVER WHO MAY DO THIS WORK — the one client-side entry point.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A QUERY AND NOT A CALCULATION
 * ═══════════════════════════════════════════════════════════════════════════
 * `assignmentEligibility.ts` next door can decide eligibility from data it is
 * handed, and it is the right tool when a component already holds a person's
 * work roles. Most assignment surfaces do not: they hold a technician roster,
 * which is a shop-scoped table with no work roles on it at all, joined to a
 * membership through a professional identity that may not have been claimed.
 *
 * Reproducing that join in the browser means reproducing the rule, and a rule
 * with two implementations has two behaviours. So this asks the database the
 * same question the assignment triggers ask, using the same functions, and the
 * answer a dropdown shows is by construction the answer a save will get.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * FAILURE IS NOT PERMISSION, AND IT IS NOT DENIAL EITHER
 * ═══════════════════════════════════════════════════════════════════════════
 * When the query fails, this returns `null` rather than an empty list or the
 * full roster, and every caller is expected to treat `null` as "eligibility is
 * unknown". Returning [] would empty a shop's board over a network blip.
 * Returning everybody would put a detailer back in the mechanical pool.
 *
 * Neither is acceptable as a silent default, so the ambiguity is handed to the
 * caller, which knows whether it is rendering a list (show everyone, let the
 * server refuse) or performing an assignment (do not).
 */
import { supabase } from '@/integrations/supabase/client';

/**
 * The technicians in a shop who may perform EVERY one of these categories.
 *
 * An empty category list requires no work family — but it is STILL asked of
 * the server (W3, field test 2026-10-16): in a shop that enforces real
 * employee eligibility, a roster-only name may not receive even an empty
 * ticket. In an observe shop the server answers "everyone", exactly as before.
 *
 * @returns technician ids, or `null` when the question could not be answered
 * (callers rendering a list then show everyone and let the server refuse).
 */
export async function fetchEligibleTechnicianIds(
  shopId: string,
  categories: readonly string[],
): Promise<Set<string> | null> {
  if (!shopId) return null;

  const distinct = [...new Set(categories.filter((c) => typeof c === 'string' && c.length > 0))];

  const { data, error } = await supabase.rpc('technicians_eligible_for_categories' as never, {
    p_shop_id: shopId,
    p_categories: distinct,
  } as never);

  if (error) {
    console.error('[eligibility] technicians_eligible_for_categories failed:', error);
    return null;
  }

  // The RPC returns a set of uuids; PostgREST renders that as either bare
  // strings or single-key rows depending on the client version, so both
  // shapes are accepted rather than assumed.
  const rows = (data ?? []) as unknown[];
  return new Set(
    rows.map((r) => (typeof r === 'string' ? r : String((r as { id?: string })?.id ?? r))),
  );
}

/**
 * The technicians who may take THIS ticket — decided by
 * `ticket_eligible_technician_ids`, the same refusal function the assignment
 * trigger runs, so a candidate offered from it is never refused on save.
 *
 * @returns technician ids, or `null` when the question could not be answered
 * (callers rendering a list then show everyone and let the server refuse).
 */
export async function fetchTicketEligibleTechnicianIds(
  ticketId: string,
): Promise<Set<string> | null> {
  if (!ticketId) return null;

  const { data, error } = await supabase.rpc('ticket_eligible_technician_ids' as never, {
    p_ticket_id: ticketId,
  } as never);

  if (error) {
    console.error('[eligibility] ticket_eligible_technician_ids failed:', error);
    return null;
  }
  const rows = (data ?? []) as unknown[];
  return new Set(
    rows.map((r) => (typeof r === 'string' ? r : String((r as { id?: string })?.id ?? r))),
  );
}

/**
 * Why this technician may not take this ticket, or `null` if they may.
 *
 * The same function the assignment trigger calls, so a screen that shows this
 * is showing the actual reason the write would fail — not a client guess at
 * it.
 */
export async function fetchAssignmentRefusal(
  technicianId: string,
  ticketId: string,
): Promise<string | null> {
  if (!technicianId || !ticketId) return null;

  const { data, error } = await supabase.rpc('technician_assignment_refusal' as never, {
    p_technician_id: technicianId,
    p_ticket_id: ticketId,
  } as never);

  if (error) {
    console.error('[eligibility] technician_assignment_refusal failed:', error);
    return null;
  }
  return (data as unknown as string | null) ?? null;
}

/**
 * Keep only the technicians who may do this work.
 *
 * When eligibility is UNKNOWN the list is returned untouched, because a
 * candidate list is a suggestion and the database is the authority: showing
 * somebody who is then refused is a worse failure than showing nobody at all
 * is, and quietly emptying the board is the worst of the three.
 */
export function applyEligibility<T extends { id: string }>(
  technicians: readonly T[],
  eligible: Set<string> | null,
): T[] {
  if (eligible === null) return [...technicians];
  return technicians.filter((t) => eligible.has(t.id));
}
